import { SelectionModel } from '@angular/cdk/collections';
import { Injectable } from '@angular/core';

import * as _ from 'lodash';
import { BehaviorSubject, Observable } from 'rxjs';
import { Path } from '../models/path';
import { TaskResolutionType, TaskStatus, TaskType } from '../models/task';

export interface TaskTypeFilter {
  type: TaskType;
  name: string;
}

export interface TaskResolutionFilter {
  type: TaskResolutionType;
  name: string;
}

const taskTypeFilters: TaskTypeFilter[] = [
  { type: TaskType.PRICE, name: 'task.type.price' },
  { type: TaskType.SALE_PRICE, name: 'task.type.salePrice' },
  { type: TaskType.HOLE, name: 'task.type.outOfStock' },
  { type: TaskType.WRONG, name: 'task.type.wrong' }];

const resolutionFilters: TaskResolutionFilter[] = [
  { type: TaskResolutionType.AUTO_RESOLVED, name: 'task.resolution.autoResolved' },
  { type: TaskResolutionType.COMPLETED, name: 'task.resolution.completed' },
  { type: TaskResolutionType.FLAGGED, name: 'task.resolution.flagged' }
];

@Injectable({
  providedIn: 'root'
})
export class TaskFilterService {
  private selectedTaskTypeFilters = new BehaviorSubject<TaskTypeFilter[]>([]);
  private taskTypeFilterSelections = new SelectionModel<TaskType>(true, []);

  private selectedPaths = new BehaviorSubject<Path[]>([]);
  private pathSelections = new SelectionModel<Path>(false, []);

  private selectedResolutionFilters = new BehaviorSubject<TaskResolutionFilter[]>([]);
  private taskResolutionSelections = new SelectionModel<TaskResolutionType>(true, []);

  private currentTasksListStatus = new BehaviorSubject<TaskStatus>(TaskStatus.ACTIVE);
  private actionableFilter = new BehaviorSubject<boolean>(false);

  constructor() {
    this.taskTypeFilterSelections.changed.subscribe(() => {
        this.notifyTaskFilterSelectionChange();
      }
    );

    this.pathSelections.changed.subscribe(() => {
        this.notifyPathSelectionChange();
      }
    );

    this.taskResolutionSelections.changed.subscribe(() => {
        this.notifyResolutionSelectionChange();
      }
    );

    this.setCurrentTasksListStatus(this.currentTasksListStatus.value);
    this.setActionableFilter(this.actionableFilter.value);
  }

  setSelectedTaskTypes(taskTypes: TaskType[]) {
    this.taskTypeFilterSelections.setSelection(...taskTypes);
  }

  addSelectedTaskType(taskType: TaskType) {
    this.taskTypeFilterSelections.select(taskType);
  }

  removeSelectedTaskType(taskType: TaskType) {
    this.taskTypeFilterSelections.deselect(taskType);
  }

  public getTaskTypeFilters(): TaskTypeFilter[] {
    return taskTypeFilters;
  }

  private notifyTaskFilterSelectionChange() {
    const selected: TaskTypeFilter[] = [];

    _.forEach(this.taskTypeFilterSelections.selected, selection => {
      selected.push(_.find(taskTypeFilters, filter => filter.type === selection));
    });

    this.selectedTaskTypeFilters.next(selected);
  }

  public getSelectedTaskTypeFilters(): Observable<TaskTypeFilter[]> {
    return this.selectedTaskTypeFilters.asObservable();
  }

  private notifyPathSelectionChange() {
    this.selectedPaths.next(this.pathSelections.selected);
  }

  setSelectedPaths(paths: Path[]) {
    this.pathSelections.setSelection(...paths);
  }

  public getSelectedPaths(): Observable<Path[]> {
    return this.selectedPaths.asObservable();
  }

  addSelectedResolution(status: TaskResolutionType) {
    this.taskResolutionSelections.select(status);
  }

  setSelectedResolution(resolutionTypes: TaskResolutionType[]) {
    this.taskResolutionSelections.setSelection(...resolutionTypes);
  }

  removeSelectedResolution(status: TaskResolutionType) {
    this.taskResolutionSelections.deselect(status);
  }

  public getResolutionFilters(): TaskResolutionFilter[] {
    return resolutionFilters;
  }

  private notifyResolutionSelectionChange() {
    const selected: TaskResolutionFilter[] = [];

    _.forEach(this.taskResolutionSelections.selected, (selection) => {
      selected.push(_.find(resolutionFilters, (filter) => filter.type === selection));
    });

    this.selectedResolutionFilters.next(selected);
  }

  public getSelectedResolutionFilters(): Observable<TaskResolutionFilter[]> {
    return this.selectedResolutionFilters.asObservable();
  }

  setCurrentTasksListStatus(status: TaskStatus) {
    this.currentTasksListStatus.next(status);
  }

  getCurrentTasksListStatus(): Observable<TaskStatus> {
    return this.currentTasksListStatus.asObservable();
  }

  setActionableFilter(state: boolean) {
    this.actionableFilter.next(state);
  }

  getActionableFilter(): Observable<boolean> {
    return this.actionableFilter.asObservable();
  }
}
